import expect from '@src/slide/infrastructure/module/independent/assert/expect';
import {
    emuToAlpha,
    emuToPt,
    emuToCanvas,
    emuToPercent,
    emuToAngle,
    alphaToOpacity,
    percentToNum, ptToPx,
    PERCENT_LINE_HEIGHT_SLIDE_TO_PPT_RATIO,
} from '@src/slide/infrastructure/module/independent/unit';
import { SolidFill } from '@src/slide/domain/ui-model/plugin/referModels/FillClass';
import { transMailStr2Obj } from '@src/slide/domain/ui-model/element-modeles/property/link/ui-sdk-transform';
import { isVerticalDirection } from '@src/slide/domain/ui-model/element-modeles/property/text/utils/text-direction';
import ImageQuickLookManager from '@src/slide/infrastructure/module/dependent/image/image-toolkit/image-quick-look-manager';

import _has from 'lodash/has';
import _set from 'lodash/set';
import { copy } from '@src/slide/infrastructure/module/independent/object-operation';
import { getColorNoAlpha } from '@src/slide/infrastructure/module/independent/color/transform';
import { TEXT_BASELINE_OFFSET } from '@src/slide/domain/ui-model/element-modeles/property/text/constants';
import { EEffectAlgn } from '@src/slide/domain/ui-model/element-modeles/property/effect/effect.interface';
import { LineEndWidthType } from '@src/slide/infrastructure/module/dependent/line-info/endStyle';
import { TextDirectionType } from '@src/slide/domain/ui-model/element-modeles/property/text/constants/text-style-type';

/**
 * 设置颜色的通用方法，将color设置到ui[key]上
 * @param {object} sdk sdk数据
 * @param {object} ui ui数据
 * @param {object} key key for ui
 */
function setColor(sdk, ui, key: string): void {
    if (_has(sdk, 'solidFill.color.srgb_color')) {
        _set(ui, key, sdk.solidFill.color.srgb_color);
    }
}

export function addPosition(sdk, ui): void {
    ['top', 'left', 'width', 'height'].forEach((key: string) => {
        if (_has(sdk, key)) _set(ui, key, emuToCanvas(sdk[key]));
    });
}

export function addTransform(sdk, ui, path: string): void {
    if (_has(sdk, 'rotate')) {
        _set(ui, `${path}.rotate`, sdk.rotate / 60000);
    } else {
        _set(ui, `${path}.rotate`, 0);
    }

    if (_has(sdk, 'flipH')) {
        _set(ui, `${path}.flipH`, sdk.flipH);
    }
    if (_has(sdk, 'flipV')) {
        _set(ui, `${path}.flipV`, sdk.flipV);
    }
}

function convertTileData(tile: any = {}, style): void {
    style.tile = {};
    style.tile.tx = tile.tx ? emuToCanvas(tile.tx) : 0; // 图片偏移量
    style.tile.ty = tile.ty ? emuToCanvas(tile.ty) : 0;
    style.tile.sx = typeof tile.sx === 'number' ? emuToPercent(tile.sx) : 100;  // 图片百分比
    style.tile.sy = typeof tile.sy === 'number' ? emuToPercent(tile.sy) : 100;
    style.tile.flip = tile.flip || 'none'; // 图片镜像，值有 none,xy,x,y
    style.tile.algn = tile.algn || 'tl';  // 对齐方式，tl,tr,bl,br,t,l,ctr,r,b
}

function getBackgroundImageForTile(sdk, imageParam): void {
    if (_has(sdk, 'image.tile')) {
        const { tile } = sdk.image;
        convertTileData(tile, imageParam);
    }
}

function getBackgroundImageSrc(sdkData): string | undefined {
    const sdkSrc = sdkData?.image?.blip?.src ?? sdkData?.image?.src;

    const { src, forceHidden } = ImageQuickLookManager.getInstance().getNextUIDataFromSDKSrc(sdkSrc);
    return forceHidden ? undefined : src;
}

export function getBackgroundImage(sdk) {
    const image: any = { src: getBackgroundImageSrc(sdk) };

    const fillRect: any = {};
    if (_has(sdk, 'image.stretch.fillRect.b')) {
        fillRect.b = sdk.image.stretch.fillRect.b / 100000 * -1;
    }
    if (_has(sdk, 'image.stretch.fillRect.t')) {
        fillRect.t = sdk.image.stretch.fillRect.t / 100000 * -1;
    }
    if (_has(sdk, 'image.stretch.fillRect.l')) {
        fillRect.l = sdk.image.stretch.fillRect.l / 100000 * -1;
    }
    if (_has(sdk, 'image.stretch.fillRect.r')) {
        fillRect.r = sdk.image.stretch.fillRect.r / 100000 * -1;
    }

    // 通过stretch属性是否存在判断平铺，存在则为非平铺，否则为平铺
    if (_has(sdk, 'image.stretch.fillRect')) {
        const stretch: any = {};
        stretch.fillRect = fillRect;
        image.stretch = stretch;
    }

    getBackgroundImageForTile(sdk, image);

    if (_has(sdk, 'image.blip.alphaModFix')) {
        image.alpha = emuToAlpha(sdk.image.blip.alphaModFix);
    }

    if (_has(sdk, 'image.rotWithShape')) {
        image.rotWithShape = sdk.image.rotWithShape;   // 0是不随形状旋转，1是随形状旋转，默认为1
    }
    return image;
}

export function addBackground(sdk, ui): void {
    let solidFill;
    if (_has(sdk, 'solidFill.color')) {  // 纯色填充，默认都存在
        solidFill = new SolidFill(sdk.solidFill.color);
    }

    let gradFill;
    if (_has(sdk, 'gradFill.gsList')) {  // 渐变填充
        gradFill = getGradualFill(sdk.gradFill);
    }

    let image;
    if (_has(sdk, 'image.blip.src')) {   // 图片填充
        image = getBackgroundImage(sdk);
    }

    let pattFill;
    if (_has(sdk, 'pattFill')) {
        pattFill = sdk.pattFill;
    }

    let grpFill;
    if (_has(sdk, 'grpFill')) {  // 是否是继承组合的填充背景，=1是
        grpFill = sdk.grpFill;
    }

    let noFill;
    if (_has(sdk, 'noFill')) {  // 是否是继承组合的填充背景，=1是
        noFill = sdk.noFill;
    }

    // 生成有原型的对象，不会递归生成observer数据
    const FillBg = function () {
        this.solidFill = solidFill;
        this.gradFill = gradFill;
        this.image = image;
        this.grpFill = grpFill;
        this.pattFill = pattFill;
        this.noFill = noFill;
    };

    // 背景设置统一放置
    const fillBackground = new FillBg();

    if (_has(sdk, 'shadeToTitle')) {
        _set(fillBackground, 'shadeToTitle', sdk.shadeToTitle);
    }

    _set(ui, 'fillBackground', fillBackground);
}

function getGradualFillByGsList(gradFill) {
    const gradObj: any = {};
    if (gradFill.gsList instanceof Array) {
        const gsList = copy(gradFill.gsList, true);
        gsList.sort((itemA, itemB) => {
            if (itemA && itemB) {
                const leftA = parseInt(itemA.pos, 10);
                const leftB = parseInt(itemB.pos, 10);
                return leftA - leftB;
            }
            return 0;
        });
        gsList.forEach((item) => {
            if (item?.pos) {
                item.pos = emuToPercent(item.pos);
            }
            if (item?.color) {
                item.color = { srgb_color: item.color.srgb_color, ...item.color };
            }
        });
        gradObj.gsList = gsList;
    }
    return gradObj;
}

const getFillFromRect = rect => ({
    b: rect.b ? emuToPercent(rect.b) : 0, l: rect.l ? emuToPercent(rect.l)
        : 0, r: rect.r ? emuToPercent(rect.r) : 0, t: rect.t ? emuToPercent(rect.t) : 0,
});

export function getGradualFill(gradFill) {
    let gradObj: any = {};
    if (_has(gradFill, 'gsList')) {  // 锚点集合
        gradObj = getGradualFillByGsList(gradFill);
    }
    if (_has(gradFill, 'lin.ang')) {  // 线性渐变
        const lin = { ang: emuToAngle(gradFill.lin.ang), scaled: gradFill.lin.scaled };
        gradObj.lin = lin;
    }
    if (_has(gradFill, 'pathGradient.path')) {  // 其他path渐变
        let rect = gradFill.pathGradient.fillToRect;
        if (!rect) {
            rect = {};
        }
        const pathGradient = {
            path: gradFill.pathGradient.path,
            fillToRect: getFillFromRect(rect),
        };
        gradObj.pathGradient = pathGradient;
    } else {
        gradObj.pathGradient = { path: 'linear' };
    }
    if (_has(gradFill, 'rotateWithShape')) {
        gradObj.rotateWithShape = gradFill.rotateWithShape;
    }
    if (_has(gradFill, 'tileRect')) {
        let rect = gradFill.tileRect;
        if (!rect) {
            rect = {};
        }
        gradObj.tileRect = getFillFromRect(rect);
    }
    return gradObj;
}

export function copyBackground(ui1, ui2) {
    if (_has(ui1, 'fillBackground')) {
        _set(ui2, 'fillBackground', ui1.fillBackground);
    }
}

/**
 * 拷贝父元素的effects到子元素上，如果重复，则
 * @param {obj} ui1 GroupModel上的effects
 * @param {obj} ui2 GroupModel的某个子元素的style
 */
export function copyEffect(ui1, ui2) {
    if (ui2.effect === null) {
        _set(ui2, 'effect', {});
    }
    if (_has(ui1, 'shadow') && !_has(ui2, 'effect.shadow')) {
        _set(ui2, 'effect.shadow', ui1.shadow);
    }
    if (_has(ui1, 'reflection') && !_has(ui2, 'effect.reflection')) {
        _set(ui2, 'effect.reflection', ui1.reflection);
    }
    if (_has(ui1, 'glow') && !_has(ui2, 'effect.glow')) {
        _set(ui2, 'effect.glow', ui1.glow);
    }
    if (_has(ui1, 'softEdge') && !_has(ui2, 'effect.softEdge')) {
        _set(ui2, 'effect.softEdge', ui1.softEdge);
    }
}

// eslint-disable-next-line complexity
export function addBorder(sdk, ui, forceTemp = false) {
    const Border = function () { };
    const border = new Border();
    if (_has(sdk, 'style')) {
        border.style = sdk.style;
    }
    if (_has(sdk, 'cap')) {
        border.linecap = sdk.cap;
    }
    if (_has(sdk, 'lineJoin')) {
        border.linejoin = sdk.lineJoin;
    }
    if (_has(sdk, 'width')) {
        border.width = emuToCanvas(sdk.width);
    }
    if (sdk?.gradFill) {
        border.gradFill = getGradualFill(sdk.gradFill);
    }
    if (_has(sdk, 'noFill')) {
        border.noFill = sdk.noFill;
    }
    if (forceTemp) {
        // 解决母版中设置文字边框不生效的问题
        if (_has(sdk, 'solidFill.color')) {
            border.tempSolidFillColor = sdk?.solidFill?.color;
            border.color = null;
        }
    } else if (_has(sdk, 'solidFill.color.srgb_color')) {
        border.color = sdk.solidFill.color.srgb_color;
        // 增加一个colorValue，用于业务判断改颜色是否从主题色继承。统一转成小写，方便判断
        border.colorValue = sdk.solidFill.color.colorValue?.toLowerCase();
    }
    if (_has(sdk, 'tailEnd.lineEndType')) {
        border.tailEnd = {
            lineEndType: sdk.tailEnd.lineEndType,
            lengthType: sdk.tailEnd.lengthType || LineEndWidthType.MIDDLE,
            widthType: sdk.tailEnd.widthType || LineEndWidthType.MIDDLE,
        };
    }
    if (_has(sdk, 'headEnd.lineEndType')) {
        border.headEnd = {
            lineEndType: sdk.headEnd.lineEndType,
            lengthType: sdk.headEnd.lengthType || LineEndWidthType.MIDDLE,
            widthType: sdk.headEnd.widthType || LineEndWidthType.MIDDLE,
        };
    }
    if (_has(sdk, 'sketchStyle')) {
        border.sketchStyle = {};
        if (_has(sdk, 'sketchStyle.type')) {
            border.sketchStyle.type = sdk.sketchStyle.type;
        }
        if (_has(sdk, 'sketchStyle.prstGeom')) {
            addBgShape(sdk.sketchStyle.prstGeom, border.sketchStyle);
        }
        if (_has(sdk, 'sketchStyle.customGeom')) {
            addCustomShape(sdk.sketchStyle.customGeom, border.sketchStyle);
        }
    }
    if (_has(sdk, 'cmpd')) {
        border.compoundType = sdk.cmpd;
    }
    ui.border = border;
}

export function convertTempSolidFillColor(borderStyle) {
    if (borderStyle.tempSolidFillColor?.srgb_color) {
        borderStyle.color = borderStyle.tempSolidFillColor?.srgb_color;
        // 增加一个colorValue，用于业务判断改颜色是否从主题色继承。统一转成小写，方便判断
        borderStyle.colorValue = borderStyle.tempSolidFillColor?.colorValue?.toLowerCase();
    }
    delete borderStyle.tempSolidFillColor;
}

// eslint-disable-next-line complexity
export function addEffect(sdk, ui, { forceTemp = false } = {}) {
    if (!sdk) {
        _set(ui, 'effect', null); return;
    }
    const effect = {};

    if (sdk.outerShadow && Object.keys(sdk.outerShadow).length) {
        addShadow(sdk.outerShadow, effect, { forceTemp, isOuter: true });
    } else if (sdk.innerShadow && Object.keys(sdk.innerShadow).length) {
        addShadow(sdk.innerShadow, effect, { forceTemp, isOuter: false });
    }

    if (sdk.glow && Object.keys(sdk.glow).length) {
        addGlow(sdk.glow, effect, forceTemp);
    }

    if (sdk.softEdge && Object.keys(sdk.softEdge).length) {
        addSoftEdge(sdk.softEdge, effect);
    }

    if (sdk.reflection && Object.keys(sdk.reflection).length) {
        addReflection(sdk.reflection, effect);
    }

    _set(ui, 'effect', effect);
}

// 用于单元测试
export function addShadowAlgn(sdk, shadow) {
    if (_has(sdk, 'algn')) {
        _set(shadow, 'algn', sdk.algn);
    } else {
        // 有负数sy但是没有algn是透视的一种场景，做下兼容
        if (sdk?.sy < 0) {
            _set(shadow, 'algn', EEffectAlgn.B);
        }
    }
}
// 用于单元测试
export function addShadowBlurRad(sdk, shadow) {
    if (_has(sdk, 'blurRad')) {
        _set(shadow, 'blurRad', emuToPt(sdk.blurRad));
    }
}
// 用于单元测试
export function addShadowDir(sdk, shadow) {
    if (_has(sdk, 'dir')) {
        _set(shadow, 'dir', emuToAngle(sdk.dir)); /* 会失真，所以需要round */
    }
}
// 用于单元测试
export function addShadowDist(sdk, shadow) {
    if (_has(sdk, 'dist')) {
        _set(shadow, 'dist', emuToPt(sdk.dist));
    }
}
// 用于单元测试
export function addShadowDotWithShape(sdk, shadow) {
    if (_has(sdk, 'rotWithShape')) {
        _set(shadow, 'rotWithShape', sdk.rotWithShape);
    }
}
// 用于单元测试
export function addShadowSize(sdk, shadow) {
    const sizeX = _has(sdk, 'sx') ? emuToPercent(sdk.sx) : null;
    const sizeY = _has(sdk, 'sy') ? emuToPercent(sdk.sy) : null;
    let size = sizeX ?? sizeY ?? 100;
    if (size < 0) {
        size = 100;
    }
    _set(shadow, 'size', size);
    _set(shadow, 'sizeX', sizeX ?? 100);
    _set(shadow, 'sizeY', sizeY ?? 100);
}

function addShadowForColor(sdk, shadow, forceTemp = false) {
    if (_has(sdk, 'shadowColor.color')) {
        const { srgb_color, alpha } = sdk.shadowColor.color;
        if (forceTemp) {
            _set(shadow, 'tempColor', sdk.shadowColor.color);
        // eslint-disable-next-line camelcase
        } else if (srgb_color) {
            _set(shadow, 'color', getColorNoAlpha(srgb_color));
        }
        _set(shadow, 'opacity', alpha === undefined ? 0 : alphaToOpacity(emuToAlpha(alpha)));
    }
}

export function convertTempShadowColor(shadowStyle) {
    if (shadowStyle?.tempColor?.srgb_color) {
        shadowStyle.color = getColorNoAlpha(shadowStyle.tempColor.srgb_color);
    }
    delete shadowStyle?.tempColor;
}
// 用于单元测试
export function addShadowSkew(sdk, shadow) {
    if (_has(sdk, 'kx')) {
        _set(shadow, 'skewX', emuToAngle(sdk.kx));
    }
    if (_has(sdk, 'ky')) {
        _set(shadow, 'skewY', emuToAngle(sdk.ky));
    }
}


export function addShadow(sdk, ui, { isOuter = true, forceTemp = false } = {}) {
    const shadow = { isOuter };

    addShadowAlgn(sdk, shadow);
    addShadowBlurRad(sdk, shadow);
    addShadowDir(sdk, shadow);
    addShadowDist(sdk, shadow);
    addShadowDotWithShape(sdk, shadow);
    addShadowForColor(sdk, shadow, forceTemp);
    addShadowSize(sdk, shadow);
    addShadowSkew(sdk, shadow);

    _set(ui, 'shadow', shadow);
}

function addGlow(sdk, ui, forceTemp = false) {
    const glow = {};

    if (_has(sdk, 'glowColor.color')) {
        const { srgb_color, alpha } = sdk.glowColor.color;
        if (forceTemp) {
            _set(glow, 'tempColor', sdk.glowColor.color);
        // eslint-disable-next-line camelcase
        } else if (srgb_color) {
            _set(glow, 'color', getColorNoAlpha(srgb_color));
        }
        _set(glow, 'opacity', expect(alpha).isNullOrUndefined() ? 0 : Math.round(alphaToOpacity(emuToAlpha(alpha))));
    }

    if (_has(sdk, 'rad')) {
        _set(glow, 'size', emuToPt(sdk.rad));
    }

    _set(ui, 'glow', glow);
}

export function convertTempGlowColor(glowStyle) {
    if (glowStyle?.tempColor?.srgb_color) {
        glowStyle.color = getColorNoAlpha(glowStyle.tempColor.srgb_color);
    }
    delete glowStyle?.tempColor;
}

function addSoftEdge(sdk, ui) {
    const softEdge = {};
    if (_has(sdk, 'rad')) {
        _set(softEdge, 'size', emuToPt(sdk.rad));
    }
    _set(ui, 'softEdge', softEdge);
}

export function addReflection(sdk, ui) {
    const reflection = {};

    if (_has(sdk, 'algn')) {
        _set(reflection, 'algn', sdk.algn);
    }
    if (_has(sdk, 'blurRad')) {
        _set(reflection, 'blurRad', emuToPt(sdk.blurRad)); /** 映像模糊度，可能是小数，UI显示 */
    }
    if (_has(sdk, 'dir')) {
        _set(reflection, 'dir', Math.round(emuToAngle(sdk.dir))); /** 一般是90度 */
    }
    if (_has(sdk, 'dist')) {
        _set(reflection, 'dist', emuToPt(sdk.dist)); /** 元素与映像的距离， UI显示 */
    }
    if (_has(sdk, 'endA')) {
        _set(reflection, 'endOpacity', parseFloat(alphaToOpacity(emuToAlpha(sdk.endA)).toFixed(1))); /** 映像结束位置的Alpha, 一般是0.003, 防止精度丢失 */
    }
    if (_has(sdk, 'endPos')) {
        _set(reflection, 'size', emuToPercent(sdk.endPos)); /** 控制映像大小，UI显示 */
    } else { /** endPos 为100时是一个缺省字段 */
        _set(reflection, 'size', 100);
    }
    if (_has(sdk, 'stA')) {
        _set(reflection, 'startOpacity', Math.round(alphaToOpacity(emuToAlpha(sdk.stA)))); /** 映像开始位置的Alpha，UI显示 */
    }
    if (_has(sdk, 'sy')) {
        _set(reflection, 'sy', sdk.sy / 100000); // 就是-1吧？？
    }
    if (_has(sdk, 'rotWithShape')) {
        _set(reflection, 'rotWithShape', sdk.rotWithShape);
    }
    _set(ui, 'reflection', reflection);
}


export function addColor(sdk, ui) {
    setColor(sdk, ui, 'color');
}

// TODO font-family是必须的
// export function addFont(sdk, ui): void {
//     // TODO 要设置多份
//     if (_has(sdk, 'font.ea.typeface') && FontMap[sdk.font.ea.typeface]) {
//         _set(ui, 'fontFamily', sdk.font.ea.typeface);
//     } else {
//         _set(ui, 'fontFamily', 'Arial'); // TODO default
//     }
//
//     // TODO 小于12
//     if (_has(sdk, 'font_size')) {
//         _set(ui, 'fontSize', sdk.font_size / 100);
//     }
// }

export function addUnderline(sdk, ui) {
    function convert(type: string): string | null {
        switch (type) {
            case 'sng':
                return 'underline';
            default:
                return null;
        }
    }

    if (_has(sdk, 'underline')) {
        _set(ui, 'textDecoration', convert(sdk.underline));
    }
}

// TODO 垂直居中flex不支持低版本ie
export function addVerticalAlign(sdk, ui) {
    if (sdk.anchor) {
        ui.display = 'flex';
        switch (sdk.anchor) {
            case 't':
                ui.alignItems = 'flex-start';
                break;
            case 'b':
                ui.alignItems = 'flex-end';
                break;
            case 'ctr':
                ui.alignItems = 'center';
                break;
            default:
                break;
        }
    }

    if (!expect(sdk.anchorCtr).isNullOrUndefined()) {
        switch (sdk.anchorCtr) {
            case 0:
                ui.justifyContent = 'start';
                break;
            case 1:
                ui.justifyContent = 'center';
                break;
            default:
                break;
        }
    }
}

export function addBgShape(sdk, ui) {
    if (_has(sdk, 'prstGeomType')) {
        _set(ui, 'bgShape', sdk.prstGeomType);
    }
    if (_has(sdk, 'adjList')) {
        const controlValues: any[] = [];
        for (let i = 0, len = sdk.adjList.length; i < len; i++) {
            controlValues.push(sdk.adjList[i].split(' ')[1] / 100000);
        }
        _set(ui, 'controlValues', controlValues);
    }
}

export function addCustomShape(sdk, ui) {
    _set(ui, 'bgShape', 'custom');
    if (_has(sdk, 'pathList') && sdk.pathList.length > 0) {
        _set(ui, 'pathList', sdk.pathList);
    }
}

export function addBgLine(sdk, ui) {
    if (_has(sdk, 'prstGeomType')) {
        if (ui?.bgLine && ui.bgLine !== sdk.prstGeomType) {
            _set(ui, 'bgLine', sdk.prstGeomType);
            _set(ui, 'controlValues', []);
        } else {
            _set(ui, 'bgLine', sdk.prstGeomType);
        }
    }
    if (_has(sdk, 'adjList') && sdk.adjList.length > 0) {
        const controlValues: any[] = [];
        for (let i = 0, len = sdk.adjList.length; i < len; i++) {
            controlValues.push(sdk.adjList[i].split(' ')[1] / 100000);
        }
        _set(ui, 'controlValues', controlValues);
    }
}

export function addConnectorInfo(sdk, ui) {
    if (_has(sdk, 'stCxn.id')) {
        _set(ui, 'start.id', sdk.stCxn.id.toString());
    }
    if (_has(sdk, 'stCxn.index')) {
        _set(ui, 'start.index', sdk.stCxn.index);
    }
    if (_has(sdk, 'endCxn.id')) {
        _set(ui, 'end.id', sdk.endCxn.id.toString());
    }
    if (_has(sdk, 'endCxn.id')) {
        _set(ui, 'end.index', sdk.endCxn.index);
    }
}

export function addLocks(sdk, ui, locks: string) {
    if (_has(sdk, locks)) {
        _set(ui.props, locks, sdk[locks]);
    }
}

export function addRelativeResize(sdk, ui) {
    if (_has(sdk, 'relativeResize')) {
        _set(ui, 'relativeResize', sdk.relativeResize);
    }
}

// eslint-disable-next-line complexity
export function convertScene3D(sdkData, uiData) {
    if (!_has(sdkData, 'props.scene3d')) {
        uiData.scene3d = undefined;
        return;
    }
    if (_has(sdkData, 'props.scene3d.camera.rotation')) {
        _set(uiData, 'scene3d.camera.rotation.x', emuToAngle(sdkData.props.scene3d.camera.rotation.x ?? 0));
        _set(uiData, 'scene3d.camera.rotation.y', emuToAngle(sdkData.props.scene3d.camera.rotation.y ?? 0));
        _set(uiData, 'scene3d.camera.rotation.z', emuToAngle(sdkData.props.scene3d.camera.rotation.z ?? 0));
    }
    if (_has(sdkData, 'props.scene3d.camera.preset')) {
        _set(uiData, 'scene3d.camera.preset', sdkData.props.scene3d.camera.preset);
    }
    if (_has(sdkData, 'props.scene3d.lightRig')) {
        if (_has(sdkData, 'props.scene3d.lightRig.rigPreset')) {
            _set(uiData, 'scene3d.lightRig.rigPreset', sdkData.props.scene3d.lightRig.rigPreset);
        }
        if (_has(sdkData, 'props.scene3d.lightRig.rotation')) {
            _set(uiData, 'scene3d.lightRig.rotation.x', emuToAngle(sdkData.props.scene3d.lightRig.rotation.x ?? 0));
            _set(uiData, 'scene3d.lightRig.rotation.y', emuToAngle(sdkData.props.scene3d.lightRig.rotation.y ?? 0));
            _set(uiData, 'scene3d.lightRig.rotation.z', emuToAngle(sdkData.props.scene3d.lightRig.rotation.z ?? 0));
        }
    }
}

// eslint-disable-next-line complexity
export function convertShape3D(sdkData, uiData) {
    if (!_has(sdkData, 'props.shape3d')) {
        uiData.shape3d = undefined;
        return;
    }
    if (_has(sdkData, 'props.shape3d.extrusionHeight')) {
        _set(uiData, 'shape3d.extrusionHeight', emuToPt(sdkData.props.shape3d.extrusionHeight ?? 0));
    }
    if (_has(sdkData, 'props.shape3d.extrusionColor')) {
        _set(uiData, 'shape3d.extrusionColor', { ...sdkData.props.shape3d.extrusionColor });
    }
    if (_has(sdkData, 'props.shape3d.materialPreset')) {
        _set(uiData, 'shape3d.materialPreset', sdkData.props.shape3d.materialPreset);
    }
    if (_has(sdkData, 'props.shape3d.bevelTop')) {
        const bevelTop = {
            width: emuToPt(sdkData.props.shape3d.bevelTop.width ?? 0) ?? 0,
            height: emuToPt(sdkData.props.shape3d.bevelTop.height ?? 0) ?? 0,
        };
        _set(uiData, 'shape3d.bevelTop', bevelTop);
    }
    if (_has(sdkData, 'props.shape3d.bevelBottom')) {
        const bevelBottom = {
            width: emuToPt(sdkData.props.shape3d.bevelBottom.width ?? 0) ?? 0,
            height: emuToPt(sdkData.props.shape3d.bevelBottom.height ?? 0) ?? 0,
        };
        _set(uiData, 'shape3d.bevelBottom', bevelBottom);
    }
}

const convertAlignItemForWrapperStyle = (anchor: string, wrapperStyle) => {
    switch (anchor) {
        case 't':
            wrapperStyle.alignItems = 'flex-start';
            break;
        case 'b':
            wrapperStyle.alignItems = 'flex-end';
            break;
        case 'ctr':
            wrapperStyle.alignItems = 'center';
            break;
        default:
            break;
    }
};

const isVertDir = (vert: TextDirectionType): boolean => isVerticalDirection(vert);

// textbody的对齐方式
export function convertTextBodyAlign(sdk, style: any = {}) {
    if (sdk.anchor) {
        style.wrapperStyle = style.wrapperStyle || {};
        convertAlignItemForWrapperStyle(sdk.anchor, style.wrapperStyle);
    }

    if (!expect(sdk.anchorCtr).isNullOrUndefined()) {
        style.wrapperStyle = style.wrapperStyle || {};
        style.bodyStyle = style.bodyStyle || {};
        const isVert = isVertDir(sdk.vert);
        switch (sdk.anchorCtr) {
            // 对齐
            default:
            case 0:
                isVert ? style.bodyStyle.height = '100%' : style.bodyStyle.width = '100%';
                break;
            // 居中
            case 1:
                if (isVert) {
                    style.bodyStyle.height = 'fit-content';
                    style.bodyStyle.maxHeight = '100%';
                } else {
                    style.bodyStyle.width = 'fit-content';
                    style.bodyStyle.maxWidth = '100%';
                }
                style.wrapperStyle.justifyContent = 'center';
                break;
        }
    }

    return style;
}

// textbody的margin
export function convertPadding(sdk, style: any = {}) {
    style.bodyStyle = style.bodyStyle || {};
    if (!expect(sdk.top_inset).isNullOrUndefined()) {
        style.bodyStyle.paddingTop = emuToPt(sdk.top_inset);
    }

    if (!expect(sdk.left_inset).isNullOrUndefined()) {
        style.bodyStyle.paddingLeft = emuToPt(sdk.left_inset);
    }

    if (!expect(sdk.right_inset).isNullOrUndefined()) {
        style.bodyStyle.paddingRight = emuToPt(sdk.right_inset);
    }

    if (!expect(sdk.bottom_inset).isNullOrUndefined()) {
        style.bodyStyle.paddingBottom = emuToPt(sdk.bottom_inset);
    }

    if (!Object.keys(style.bodyStyle).length) {
        delete style.bodyStyle;
    }

    return style;
}

export function convertFlatText(sdk, style: any = {}) {
    if (!expect(sdk.isFlatText).isNullOrUndefined()) {
        style.bodyStyle = style.bodyStyle || {};
        style.bodyStyle.isFlatText = sdk.isFlatText === 1;
    }
    return style;
}

export function convertBold(sdk, setStyle) {
    const style = setStyle;
    if (_has(sdk, 'bold')) {
        switch (sdk.bold) {
            case 1:
                style.fontWeight = 'bold';
                break;
            case 0:
                style.fontWeight = 'normal';
                break;
            default:
                break;
        }
    }
}

export function convertItalic(sdk, setStyle) {
    const style = setStyle;
    if (_has(sdk, 'italic')) {
        switch (sdk.italic) {
            case 1:
                style.fontStyle = 'italic';
                break;
            case 0:
                style.fontStyle = 'normal';
                break;
            default:
                break;
        }
    }
}

export function convertFontSize(sdk, styleParam) {
    if (_has(sdk, 'font_size')) {
        styleParam.fontSize = sdk.font_size / 100;
    }
}

export function convertBaseLineOffset(sdk, styleParam = {}) {
    if (_has(sdk, 'baseline')) {
        styleParam[TEXT_BASELINE_OFFSET] = percentToNum(sdk.baseline);
    }
    return styleParam;
}

export function convertFont(sdk, styleObj: any = {}) {
    const style = styleObj;
    // 获取所有语言的默认值
    if (_has(sdk, 'font')) {
        style.fonts = Object.keys(sdk.font)
            .filter(key => key !== 'lang')
            .map(key => ({
                key,
                fontFamily: sdk.font[key].typeface,
            }))
            .reduce((prev, curr) => ({
                ...prev,
                [curr.key]: curr.fontFamily,
            }), {});
    }

    // 文字语言类型
    if (_has(sdk, 'lang')) {
        switch (sdk.lang) {
            case 'en-US':
            case 'en':
                style.lang = 'latin';
                break;
            case 'zh-CN':
                style.lang = 'ea';
                break;
            default:
                style.lang = sdk.lang;
                break;
        }
    }

    convertFontSize(sdk, style);

    convertBold(sdk, style);

    if (_has(sdk, 'thick')) {
        switch (sdk.thick) {
            case 1:
                style.fontWeight = 'thick';
                break;
            default:
                break;
        }
    }

    convertItalic(sdk, style);

    return style;
}

export function convertColor(sdk, setStyle: any = {}) {
    const style = setStyle;
    if (_has(sdk, 'solidFill.color')) {
        const colorObj = sdk.solidFill.color;
        if (sdk.forceTemp) {
            style.tempColor = colorObj;
        } else if (colorObj.srgb_color) {
            style.color = colorObj.srgb_color;
        }
    }
    return style;
}

export function convertTempColor(setStyle: any = {}) {
    const style = setStyle;
    if (style.tempColor) {
        if (style.tempColor.srgb_color) {
            style.color = style.tempColor.srgb_color;
        }
        delete style.tempColor;
    }

    return style;
}

export function convertTextAlign(sdk, style: any = {}) {
    switch (sdk.alignment) {
        case 'l':
            style.textAlign = 'left';
            break;
        case 'ctr':
            style.textAlign = 'center';
            break;
        case 'r':
            style.textAlign = 'right';
            break;
        case 'just':
            style.textAlign = 'justify';
            break;
        case 'dist':
            style.textAlign = 'distribute';
    }

    return style;
}

export function convertUnderline(sdk, style: any = {}) {
    function convert(type: string): string | null {
        switch (type) {
            case 'sng':
                return 'underline';
            default:
                return null;
        }
    }

    if (_has(sdk, 'underline')) {
        style.textDecoration = convert(sdk.underline);
    }

    return style;
}

// 这玩意不是一个css style
export function convertStrike(sdk, style: any = {}) {
    function convert(type: string): string | null {
        switch (type) {
            case 'sngStrike':
                return 'sng';
            case 'dblStrike':
                return 'dbl';
            case 'noStrike':
                return 'none';
            default:
                return null;
        }
    }

    if (_has(sdk, 'strike')) {
        style.del = convert(sdk.strike);
    }

    return style;
}

// 这玩意不是一个css style
export function convertTab(sdk, style: any = {}) {
    if (_has(sdk, 'defTablSz')) {
        style.tabSize = emuToPt(sdk.defTablSz);
    }

    return style;
}

export function convertLetterSpace(sdk, style: any = {}) {
    if (_has(sdk, 'spc')) {
        style.letterSpacing = sdk.spc / 100;
    }

    return style;
}

export function convertLineSpace(sdk, style: any = {}) {
    // TODO 好像有些不对 第一行没有？
    if (_has(sdk, 'spcBef')) {
        if (!expect(sdk.spcBef.spcPts).isNullOrUndefined()) {
            style.paddingTop = sdk.spcBef.spcPts / 100;
            style.paddingTopIs = 'absolute';
        }

        if (!expect(sdk.spcBef.spcPct).isNullOrUndefined()) {
            style.paddingTop = percentToNum(sdk.spcBef.spcPct);
            style.paddingTopIs = 'percent';
        }
    }

    if (_has(sdk, 'spcAft')) {
        if (!expect(sdk.spcAft.spcPts).isNullOrUndefined()) {
            style.paddingBottom = sdk.spcAft.spcPts / 100;
            style.paddingBottomIs = 'absolute';
        }

        if (!expect(sdk.spcAft.spcPct).isNullOrUndefined()) {
            style.paddingBottom = percentToNum(sdk.spcAft.spcPct);
            style.paddingBottomIs = 'percent';
        }
    }

    if (_has(sdk, 'lnSpc')) {
        if (!expect(sdk.lnSpc.spcPct).isNullOrUndefined()) {
            // office 默认行高是字体1.2倍
            // TODO lineheight设置成小数会丢值
            style.PPTLineHeight = percentToNum(sdk.lnSpc.spcPct);
            style.lineHeight = style.PPTLineHeight * PERCENT_LINE_HEIGHT_SLIDE_TO_PPT_RATIO;
            style.lineHeightIs = 'percent';
        }

        if (!expect(sdk.lnSpc.spcPts).isNullOrUndefined()) {
            style.PPTLineHeight = null;
            style.lineHeight = sdk.lnSpc.spcPts / 100;
            style.lineHeightIs = 'absolute';
        }
    }

    return style;
}

export function convertWrap(sdk, style: any = {}) {
    if (sdk.wrap) {
        style.bodyStyle = style.bodyStyle || {};
        switch (sdk.wrap) {
            case 'square':
                style.bodyStyle.whiteSpace = 'pre-wrap';
                break;
            case 'none':
                style.bodyStyle.whiteSpace = 'pre';
                break;
            default:
                break;
        }
    }

    return style;
}

// 文字背景色
export function convertHighlight(sdk, setStyle: any = {}) {
    const style = setStyle;
    if (_has(sdk, 'highlight.color')) {
        const colorObj = sdk.highlight.color;
        if (sdk.forceTemp) {
            style.tempBgColor = colorObj;
        } else if (colorObj.srgb_color) {
            style.backgroundColor = colorObj.srgb_color;
        }
    }

    return style;
}

export function convertTempBgColor(setStyle: any = {}) {
    const style = setStyle;
    if (style.tempBgColor) {
        if (style.tempBgColor.srgb_color) {
            style.backgroundColor = style.tempBgColor.srgb_color;
        }
        delete style.tempBgColor;
    }

    return style;
}

export function convertPosition(sdk, style: any = {}) {
    if (_has(sdk, 'marginLeft')) {
        style.marginLeft = emuToPt(sdk.marginLeft);
    }

    return style;
}

export function convertAutoFit(sdk, style: any = {}) {
    if (sdk.autoFitType) {
        style.autoFitType = sdk.autoFitType;
    }

    return style;
}

function convertColumnProps(sdk, style: any = {}) {
    if (sdk.numCol) {
        style.wrapperStyle = style.wrapperStyle || {};
        style.wrapperStyle.columnNumber = sdk.numCol;
    }
    if (sdk.spcCol) {
        style.wrapperStyle = style.wrapperStyle || {};
        // 为了避免单位转换过程中带来的精度损失，在 ui 中也使用 emu 单位，只在渲染和展示的时候做转换
        style.wrapperStyle.columnSpace = sdk.spcCol;
    }
    return style;
}

function convertFontScale(sdk, style: any = {}) {
    if (sdk.fontScale) {
        style.wrapperStyle = style.wrapperStyle || {};
        style.wrapperStyle.fontScale = percentToNum(sdk.fontScale);
    }
    return style;
}

export function convertIndent(sdk, style: any = {}) {
    if (!expect(sdk.indent).isNullOrUndefined()) {
        style.textIndent = emuToPt(sdk.indent);
    }
    return style;
}

const convertUrl = (sdk, data): void => {
    const { url } = sdk.hlink;
    if (url && url.indexOf('mailto') >= 0) {
        data.mail = transMailStr2Obj(url);
    } else {
        data.url = url;
    }
};

const convertBaseLink = (sdk) => {
    const data: any = {};
    if (sdk.hlinkClick.indexOf('mailto') >= 0) {
        data.mail = transMailStr2Obj(sdk.hlinkClick);
    } else {
        data.url = sdk.hlinkClick;
    }
    return data;
};

export function convertLink(sdk, style: any = {}) {
    if (sdk.hlink) {
        const data: any = {};
        if (_has(sdk.hlink, 'url')) {
            convertUrl(sdk, data);
        }
        if (_has(sdk.hlink, 'slideJump')) {
            data.slideJump = sdk.hlink.slideJump ? copy(sdk.hlink.slideJump, true) : null;
        }
        if (_has(sdk.hlink, 'tooltip')) {
            data.tooltip = sdk.hlink.tooltip;
        }
        if (_has(sdk.hlink, 'colorType')) {
            data.colorType = sdk.hlink.colorType;
        }
        if (_has(sdk.hlink, 'mimeType')) {
            data.mimeType = sdk.hlink.mimeType;
        }
        if (_has(sdk.hlink, 'iconMode')) {
            data.iconMode = sdk.hlink.iconMode;
        }
        style.href = data;
    } else if (sdk.hlinkClick) {   // 兼容老逻辑
        style.href = convertBaseLink(sdk);
    }
    return style;
}

export function convertCharacter(sdk, style: any = {}) {
    // 项目符号颜色相关
    convertCharacterColor(sdk, style);

    // 项目符号跟随相关
    if (sdk.buFontTx) {
        style.buFontTx = sdk.buFontTx;
    }

    // 项目符号大小
    convertCharacterSize(sdk, style);

    if (!expect(sdk.character_bullet_char).isNullOrUndefined()) {
        style.character = true;
        style.character_bullet_char = sdk.character_bullet_char;
    }

    if (!expect(sdk.buAutoNum).isNullOrUndefined()) {
        style.character = true;
        style.buAutoNum = sdk.buAutoNum;
    }

    if (!expect(sdk.buBlip).isNullOrUndefined()) {
        style.character = true;
        style.buBlip = sdk.buBlip;
    }

    if (!expect(sdk.buNone).isNullOrUndefined()) {
        // 看其他地方对 buNone 属性的注释，buNone === 0 表示有项目符号，buNone === 1 表示没有项目符号
        // 看了下这个属性应该是存在了好几年了，属于历史逻辑，我就按照这个逻辑修改 bug 了，只有在 buNone === 0 时才设置 character 为 true
        if (sdk.buNone === 0) {
            style.character = true;
        }
        style.buNone = sdk.buNone;
    }

    if (sdk.buFont) {
        style.buFont = JSON.parse(JSON.stringify(sdk.buFont));
    }
    return style;
}

function convertCharacterSize(sdk, style): void {
    if (sdk.buSzTx) {
        style.buSzTx = sdk.buSzTx;
    }
    // 项目符号大小相关
    if (sdk.buSzPct) {
        style.buSzPct = percentToNum(sdk.buSzPct);
    }
    if (sdk.buSzPts) {
        // 转成px处理
        style.buSzPts = ptToPx(sdk.buSzPts);
    }
}

function convertCharacterColor(sdk, style): void {
    // 项目符号颜色
    if (sdk.buClr) {
        style.buClr = sdk.buClr;
    }
    if (sdk.buClrTx) {
        style.buClrTx = sdk.buClrTx;
    }
}

// 转换文本方向
export function convertTextDirection(sdk, style) {
    if (_has(sdk, 'vert')) {
        style.wrapperStyle = style.wrapperStyle || {};
        style.wrapperStyle.textDirection = sdk.vert;
    }

    return style;
}

export function convertAddon(sdk, setStyle: any = {}) {
    const style = setStyle;
    if (_has(sdk, 'displayMode')) {
        style.displayMode = sdk.displayMode;
    }
    if (_has(sdk, 'embedUrl')) {
        style.embedUrl = sdk.embedUrl;
    }
    if (_has(sdk, 'iframeId')) {
        style.iframeId = sdk.iframeId;
    }
    if (_has(sdk, 'extraData')) {
        style.extraData = sdk.extraData;
    }
    if (_has(sdk, 'title')) {
        style.title = sdk.title;
    }
    if (_has(sdk, 'originUrl')) {
        style.originUrl = sdk.originUrl;
    }
    if (_has(sdk, 'aid')) {
        style.aid = sdk.aid;
    }
    if (_has(sdk, 'type')) {
        style.type = sdk.type;
    }
    if (_has(sdk, 'docElement')) {
        style.docElement = sdk.docElement;
    }
    return style;
}

/**
 * 文字渐变
 * @param sdk
 * @param ui
 */
export const convertTextFill = (sdk, ui = {}) => {
    if (sdk.gradFill) {
        _set(ui, 'gradFill', getGradualFill(sdk.gradFill));
    }
    if (sdk.image) {
        _set(ui, 'image', getBackgroundImage(sdk));
    }
    if (sdk.pattFill) {
        _set(ui, 'pattFill', sdk.pattFill);
    }
    if (sdk.noFill) {
        _set(ui, 'noFill', sdk.noFill);
    }
    return ui;
};

export const convertTextEffectList = (sdk, ui = {}) => {
    if (sdk.effectList) {
        addEffect(sdk.effectList, ui, { forceTemp: sdk.forceTemp });
    }
    return ui;
};

export const unitTestObj = {
    addBorder,
};
export const convertTextStroke = (sdk, ui = {}) => {
    if (sdk?.outline) {
        // 为了单元测试
        unitTestObj.addBorder(sdk.outline, ui, sdk.forceTemp);
    }
    return ui;
};

function convertResizeLevel(sdk, style: any = {}) {
    if (!expect(sdk.resizeLevel).isNullOrUndefined()) {
        style.wrapperStyle = style.wrapperStyle || {};
        style.wrapperStyle.resizeLevel = sdk.resizeLevel;
    }
    return style;
}

function emptyConvertFun(sdk, style = {}) {
    return style;
}

function convertTabList(sdkData, style) {
    if (_has(sdkData, 'tabList') && Array.isArray(sdkData.tabList)) {
        const { tabList } = sdkData;
        const alignmentMapping = {
            l: 'left',
            r: 'right',
            ctr: 'center',
            dec: 'decimal',
        };
        const tabListStyle = tabList.map(tabstop => ({
            position: emuToPt(tabstop.pos),
            alignment: alignmentMapping[tabstop.algn],
        }));
        _set(style, 'tabList', tabListStyle);
    }
    return style;
}

export function convertStyle(sdk) {
    // 不采用函数式的方式了，参见原本的convert，deepMerge损耗过大
    return [
        convertPadding,
        convertFlatText,
        convertTextBodyAlign,
        convertTextAlign,
        convertFont,
        convertUnderline,
        convertStrike,
        convertColor,
        convertPosition,
        convertWrap,
        convertLetterSpace,
        convertTab,
        convertLineSpace,
        convertHighlight,
        (sdk.forceTemp) ? convertCharacter : emptyConvertFun,  // 不在这里转项目符号，后面的逻辑会转。
        convertAutoFit,
        convertColumnProps,
        convertIndent,
        convertLink,
        convertAddon,
        convertTextFill,
        convertResizeLevel,
        convertFontScale,
        convertTextEffectList,
        convertTextStroke,
        convertTextDirection,
        convertTabList,
        convertBaseLineOffset,
    ].reduce((prev, curr) => curr(sdk, prev), {});
}

export const testObj = {
    convertAlignItemForWrapperStyle,
    convertUrl,
    convertFontScale,
};
